import dagre from '@dagrejs/dagre';
import type { Edge, Node } from 'reactflow';

const NODE_WIDTH = 320;
const NODE_HEIGHT = 220;

export function layoutGraph<T>(nodes: Node<T>[], edges: Edge[]) {
  const dagreGraph = new dagre.graphlib.Graph();

  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({
    rankdir: 'LR', // Left → Right
    acyclicer: 'greedy',
    ranksep: 220, // Distance between columns
    nodesep: 80, // Distance between nodes in same column
    edgesep: 30,
    marginx: 40,
    marginy: 40,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
    });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const positionedNodes = nodes.map((node, index) => {
    const pos = dagreGraph.node(node.id);

    return {
      ...node,
      position: pos
        ? {
            x: pos.x - NODE_WIDTH / 2,
            y: pos.y - NODE_HEIGHT / 2,
          }
        : {
            x: 0,
            y: index * (NODE_HEIGHT + 40),
          },
    };
  });

  const incomingCount = new Map(nodes.map((n) => [n.id, 0]));
  const outgoingCount = new Map(nodes.map((n) => [n.id, 0]));

  edges.forEach((e) => {
    incomingCount.set(e.target, (incomingCount.get(e.target) ?? 0) + 1);
    outgoingCount.set(e.source, (outgoingCount.get(e.source) ?? 0) + 1);
  });

  const startId =
    nodes.find((n) => (incomingCount.get(n.id) ?? 0) === 0)?.id ?? null;

  const endIds = nodes
    .filter((n) => (outgoingCount.get(n.id) ?? 0) === 0)
    .map((n) => n.id);

  const brokenNodeIds = nodes
    .filter((node) => {
      const inCount = incomingCount.get(node.id) ?? 0;
      const outCount = outgoingCount.get(node.id) ?? 0;

      return inCount === 0 && outCount === 0;
    })
    .map((node) => node.id);

  const orphanStartIds = nodes
    .filter((node) => {
      const inCount = incomingCount.get(node.id) ?? 0;
      return inCount === 0 && node.id !== startId;
    })
    .map((node) => node.id);

  return {
    nodes: positionedNodes,
    startId,
    endIds,
    incomingCount,
    outgoingCount,
    brokenNodeIds,
    orphanStartIds,
  };
}
